import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  Hammer,
  AlertTriangle,
  Camera,
  ZoomIn,
  X,
  CheckCircle2,
  Loader2,
  FileText,
  Tag,
  DollarSign,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { Modal, ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { usePermissions } from '@/hooks/usePermissions';
import { materialReceiptService } from '../../services/materialReceiptService';
import { ProductProjectPhotoThumb } from '@components/ProductProjectPhotoThumb';
import {
  MaterialReceipt,
  MATERIAL_RECEIPT_STATUS,
  MATERIAL_RECEIPT_STATUS_LABELS,
  MaterialReceiptStatus,
} from '../../types/materialReceipt';
import { FinancialManagementModal } from './FinancialManagementModal';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (v?: number) =>
  v !== undefined && v !== null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('pt-BR');
};
// ─── DamagePhotoThumb ──────────────────────────────────────────────────
// Busca o binário via endpoint proxy (não usa damage_photo_url diretamente).

interface DamagePhotoThumbProps {
  receiptId: number;
  itemId: number;
  compact?: boolean;
  onOpenLightbox: (objectUrl: string) => void;
}

const DamagePhotoThumb: React.FC<DamagePhotoThumbProps> = ({ receiptId, itemId, compact = false, onOpenLightbox }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let revoked = false;
    materialReceiptService.getDamagePhoto(receiptId, itemId)
      .then(url => { if (!revoked) setObjectUrl(url); })
      .catch(() => { if (!revoked) setLoadError(true); });
    return () => {
      revoked = true;
      setObjectUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [receiptId, itemId]);

  if (loadError) return null;
  if (!objectUrl) return <Loader2 size={14} className="animate-spin text-slate-400" />;

  if (compact) {
    return (
      <button
        className="p-0.5 text-slate-400 hover:text-slate-600"
        title="Ver foto"
        onClick={() => onOpenLightbox(objectUrl)}
      >
        <Camera size={12} />
      </button>
    );
  }

  return (
    <button
      className="relative group shrink-0"
      onClick={() => onOpenLightbox(objectUrl)}
      title="Ver foto em tamanho completo"
    >
      <img
        src={objectUrl}
        alt="Foto avaria"
        className="w-16 h-16 object-cover rounded border"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 rounded transition-opacity">
        <ZoomIn size={18} className="text-white" />
      </span>
    </button>
  );
};

// ─── Received Photo Thumb (produto personalizado) ────────────────────

const ReceivedPhotoThumb: React.FC<{
  receiptId: number;
  itemId: number;
  photoId: number;
  onOpenLightbox: (objectUrl: string) => void;
}> = ({ receiptId, itemId, photoId, onOpenLightbox }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let revoked = false;
    materialReceiptService.getReceivedPhotoFile(receiptId, itemId, photoId)
      .then(url => { if (!revoked) setObjectUrl(url); })
      .catch(() => { if (!revoked) setLoadError(true); });
    return () => {
      revoked = true;
      setObjectUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [receiptId, itemId, photoId]);

  if (loadError) return null;
  if (!objectUrl) return <Loader2 size={14} className="animate-spin text-slate-400" />;

  return (
    <button
      className="relative group shrink-0"
      onClick={() => onOpenLightbox(objectUrl)}
      title="Ver foto em tamanho completo"
    >
      <img src={objectUrl} alt="Foto do recebimento" className="w-16 h-16 object-cover rounded border" />
      <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 rounded transition-opacity">
        <ZoomIn size={18} className="text-white" />
      </span>
    </button>
  );
};

// ─── Status Badge ────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: MaterialReceiptStatus }> = ({ status }) => {
  const isConfirmed = status === MATERIAL_RECEIPT_STATUS.CONFIRMED;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
        isConfirmed
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-amber-100 text-amber-700'
      }`}
    >
      {isConfirmed && <CheckCircle2 size={12} />}
      {MATERIAL_RECEIPT_STATUS_LABELS[status] ?? String(status)}
    </span>
  );
};
// ─── Field display ───────────────────────────────────────────────

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-xs text-slate-500 uppercase tracking-wider">{label}</dt>
    <dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
  </div>
);

// ─── Component ───────────────────────────────────────────────────

export const MaterialReceiptDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { checkPermission } = usePermissions();

  const [receipt, setReceipt] = useState<MaterialReceipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // lightboxUrl stores a blob: object URL (never a raw blob storage URL)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [labelsError, setLabelsError] = useState<string | null>(null);
  const [financialModalOpen, setFinancialModalOpen] = useState(false);

  // Check if user is admin or finance
  const canAccessFinancialManagement = checkPermission('*').hasPermission || checkPermission('finance.manage').hasPermission;

  const openLightbox = useCallback((objectUrl: string) => {
    setLightboxUrl(objectUrl);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxUrl(null);
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const data = await materialReceiptService.getMaterialReceiptById(parseInt(id));
        setReceipt(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar recebimento');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      await materialReceiptService.deleteMaterialReceipt(parseInt(id));
      navigate('/material-receipts');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir recebimento');
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmReceipt = async () => {
    if (!id || !receipt) return;
    try {
      setConfirming(true);
      setConfirmError(null);
      const updated = await materialReceiptService.updateMaterialReceiptStatus(
        parseInt(id),
        { status: MATERIAL_RECEIPT_STATUS.CONFIRMED },
      );
      setReceipt(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao confirmar recebimento';
      if (msg.includes('já confirmado')) {
        setConfirmError('Este recebimento já foi confirmado.');
      } else {
        setConfirmError(msg);
      }
    } finally {
      setConfirming(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!id) return;
    try {
      setPdfLoading(true);
      setPdfError(null);
      await materialReceiptService.downloadPdf(parseInt(id));
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Falha ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadLabels = async () => {
    if (!id) return;
    try {
      setLabelsLoading(true);
      setLabelsError(null);
      await materialReceiptService.downloadLabels(parseInt(id));
    } catch (err) {
      setLabelsError(err instanceof Error ? err.message : 'Falha ao gerar etiquetas');
    } finally {
      setLabelsLoading(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/material-receipts')}><ArrowLeft size={20} /></Button>
          <div className="h-8 bg-slate-200 rounded w-56 animate-pulse" />
        </div>
        {[...Array(4)].map((_, i) => (
          <Card key={i}><CardContent><div className="h-24 bg-slate-200 rounded animate-pulse" /></CardContent></Card>
        ))}
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/material-receipts')}><ArrowLeft size={20} /></Button>
          <h1 className="text-2xl font-bold text-slate-900">Recebimento não encontrado</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error || 'Recebimento não encontrado.'}</div>
      </div>
    );
  }

  const items = receipt.items || [];
  const installments = receipt.installments || [];
  const isConfirmed = receipt.status === MATERIAL_RECEIPT_STATUS.CONFIRMED;

  // Determine if there are linked sales orders (items with order_id)
  const linkedSalesOrderIds = [...new Set(items.filter(i => i.order_id).map(i => i.order_id!))];
  const hasLinkedSalesOrders = linkedSalesOrderIds.length > 0;
  const linkedOrdersForModal = linkedSalesOrderIds.map(orderId => ({
    order_id: orderId,
    company_id: undefined as number | undefined, // Will be fetched by modal if needed
  }));

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/material-receipts')}><ArrowLeft size={20} /></Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">
                Recebimento #{receipt.material_receipt_id}
              </h1>
              <StatusBadge status={receipt.status ?? MATERIAL_RECEIPT_STATUS.IN_REVIEW} />
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Criado em {formatDateTime(receipt.created_at)} por {receipt.created_by}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <PermissionGuard permission="material_receipts.read">
            <Button variant="outline" icon={<FileText size={18} />} loading={pdfLoading} onClick={handleDownloadPdf}>
              Gerar PDF
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="material_receipts.read">
            <Button variant="outline" icon={<Tag size={18} />} loading={labelsLoading} onClick={handleDownloadLabels}>
              Gerar Etiquetas
            </Button>
          </PermissionGuard>
          {canAccessFinancialManagement && hasLinkedSalesOrders && (
            <Button
              variant="outline"
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              icon={<DollarSign size={18} />}
              onClick={() => setFinancialModalOpen(true)}
            >
              Gestão Financeira
            </Button>
          )}
          <PermissionGuard permission="material_receipts.write">
            {!isConfirmed && (
              <Button
                icon={confirming ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                onClick={handleConfirmReceipt}
                disabled={confirming}
              >
                {confirming ? 'Confirmando...' : 'Confirmar Recebimento'}
              </Button>
            )}
          </PermissionGuard>
          <PermissionGuard permission="material_receipts.write">
            {!isConfirmed && (
              <Button variant="outline" icon={<Edit size={18} />} onClick={() => navigate(`/material-receipts/${id}/edit`)}>
                Editar
              </Button>
            )}
          </PermissionGuard>
          {!isConfirmed && (
            <PermissionGuard permission="material_receipts.delete">
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" icon={<Trash2 size={18} />} onClick={() => setDeleteOpen(true)}>
                Excluir
              </Button>
            </PermissionGuard>
          )}
        </div>
      </div>

      {pdfError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {pdfError}
        </div>
      )}

      {labelsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {labelsError}
        </div>
      )}

      {confirmError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {confirmError}
        </div>
      )}

      {/* ── Dados Gerais ──────────────────────────────────────── */}
      <Card>
        <CardHeader title="Dados Gerais" />
        <CardContent>
          {/* Status — destaque interativo */}
          <div className={`mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border ${
            isConfirmed
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Status do Recebimento</p>
              <StatusBadge status={receipt.status ?? MATERIAL_RECEIPT_STATUS.IN_REVIEW} />
              {isConfirmed ? (
                <p className="text-xs text-emerald-700 mt-1">Estoque processado pelo sistema.</p>
              ) : (
                <p className="text-xs text-amber-700 mt-1">Aguardando conferência da logística.</p>
              )}
            </div>
            <PermissionGuard permission="material_receipts.write">
              {!isConfirmed && (
                <Button
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-100 shrink-0"
                  variant="outline"
                  icon={confirming ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  onClick={handleConfirmReceipt}
                  disabled={confirming}
                >
                  {confirming ? 'Confirmando...' : 'Confirmar Recebimento'}
                </Button>
              )}
            </PermissionGuard>
          </div>

          <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Fornecedor">
              {receipt.provider_name ? (
                <>
                  <span className="font-medium">{receipt.provider_name}</span>
                  {receipt.provider_document && <span className="block text-xs text-slate-400">{receipt.provider_document}</span>}
                </>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </Field>
            <Field label="Cliente">
              {receipt.customer_name ? (
                <span className="font-medium">{receipt.customer_name}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </Field>
            <Field label="Data do Recebimento">{formatDate(receipt.receipt_date)}</Field>
            <Field label="Transportadora">{receipt.carrier || '—'}</Field>
            <Field label="Volumes">{receipt.volumes ?? '—'}</Field>
            <Field label="Última Atualização">{formatDateTime(receipt.updated_at)}</Field>
          </dl>
        </CardContent>
      </Card>

      {/* ── Itens ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader title={`Itens do Recebimento (${items.length})`} />
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhum item neste recebimento.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="py-2 px-4">Tipo</th>
                    <th className="py-2 px-4">Descrição</th>
                    <th className="py-2 px-4">Unid.</th>
                    <th className="py-2 px-4 text-right">Valor Unit.</th>
                    <th className="py-2 px-4 text-right">Qtde</th>
                    <th className="py-2 px-4 text-right">Subtotal</th>
                    <th className="py-2 px-4">Pedido</th>
                    <th className="py-2 px-4">Avaria</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const isRaw = !!item.raw_material_id;
                    return (
                      <tr key={item.material_receipt_item_id || idx} className={`border-b border-slate-100 ${item.damaged ? 'bg-red-50' : ''}`}>
                        <td className="py-2 px-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${isRaw ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                            {isRaw ? <Hammer size={12} /> : <Package size={12} />}
                            {isRaw ? 'MP' : 'Prod'}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-slate-900">
                          {item.description}
                          {item.is_custom_product && (
                            <span className="block text-xs text-pink-700 mt-0.5">Produto personalizado</span>
                          )}
                        </td>
                        <td className="py-2 px-4">{item.unit || '—'}</td>
                        <td className="py-2 px-4 text-right">{formatCurrency(item.unit_value)}</td>
                        <td className="py-2 px-4 text-right">{item.quantity}</td>
                        <td className="py-2 px-4 text-right font-medium">{formatCurrency(item.unit_value * item.quantity)}</td>
                        <td className="py-2 px-4 text-slate-600">
                          {item.purchase_order_number
                            ? `PC #${item.purchase_order_number}`
                            : item.purchase_order_id
                              ? `PC #${item.purchase_order_id}`
                              : item.order_id
                                ? <span className="text-emerald-600 font-medium">PV #{item.order_id}</span>
                                : '—'}
                          {(item.purchase_order_sales_orders?.length ?? 0) > 0 && (
                            <span className="block text-xs text-emerald-600 mt-0.5">
                              → {item.purchase_order_sales_orders!.map(l => `PV #${l.order_id}${l.customer_name ? ` (${l.customer_name})` : ''}`).join(', ')}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-4">
                          {item.damaged ? (
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1.5">
                                <AlertTriangle size={14} className="text-red-500 shrink-0" />
                                <span className="text-xs text-red-600 font-medium">Avariado</span>
                                {item.damage_photo_url && item.material_receipt_item_id && (
                                  <DamagePhotoThumb
                                    receiptId={receipt.material_receipt_id}
                                    itemId={item.material_receipt_item_id}
                                    compact
                                    onOpenLightbox={openLightbox}
                                  />
                                )}
                              </div>
                              {item.damage_notes && (
                                <p className="text-xs text-slate-500 max-w-[160px] truncate" title={item.damage_notes}>
                                  {item.damage_notes}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-emerald-600">OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300">
                    <td colSpan={5} className="py-2 px-4 text-right font-semibold text-slate-700">Total:</td>
                    <td className="py-2 px-4 text-right font-bold text-emerald-700">
                      {formatCurrency(items.reduce((s, i) => s + i.unit_value * i.quantity, 0))}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Avarias detalhadas ────────────────────────────────── */}
      {items.some(i => i.damaged) && (
        <Card>
          <CardHeader title="Itens com Avaria" />
          <CardContent>
            <div className="space-y-3">
              {items.filter(i => i.damaged).map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle size={20} className="text-red-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{item.description}</p>
                    <p className="text-sm text-red-700 mt-1">{item.damage_notes || 'Sem observações de avaria.'}</p>
                  </div>
                  {item.damage_photo_url && item.material_receipt_item_id && (
                    <DamagePhotoThumb
                      receiptId={receipt.material_receipt_id}
                      itemId={item.material_receipt_item_id}
                      onOpenLightbox={openLightbox}
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Produtos personalizados recebidos ─────────────────── */}
      {items.some(i => i.is_custom_product) && (
        <Card>
          <CardHeader title="Produtos Personalizados — Conferência" />
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">
              Compare as fotos do que foi recebido pela logística com a Foto do Projeto aprovada pela cliente.
            </p>
            <div className="space-y-4">
              {items.filter(i => i.is_custom_product).map((item, idx) => (
                <div key={item.material_receipt_item_id ?? idx} className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="font-medium text-slate-900 mb-2">{item.description}</p>
                  <div className="flex flex-wrap items-start gap-6">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Foto do projeto</p>
                      {item.has_project_photo && item.product_id ? (
                        <ProductProjectPhotoThumb productId={item.product_id} />
                      ) : (
                        <span className="text-xs text-slate-400 italic">Sem foto do projeto no cadastro do produto</span>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">
                        Fotos do recebimento ({item.received_photos?.length ?? 0})
                      </p>
                      {(item.received_photos?.length ?? 0) > 0 && item.material_receipt_item_id ? (
                        <div className="flex flex-wrap gap-2">
                          {item.received_photos!.map(photo => (
                            photo.material_receipt_item_received_photo_id ? (
                              <ReceivedPhotoThumb
                                key={photo.material_receipt_item_received_photo_id}
                                receiptId={receipt.material_receipt_id}
                                itemId={item.material_receipt_item_id!}
                                photoId={photo.material_receipt_item_received_photo_id}
                                onOpenLightbox={openLightbox}
                              />
                            ) : null
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">Nenhuma foto anexada pela logística</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Parcelas ──────────────────────────────────────────── */}
      <Card>
        <CardHeader title={`Parcelas (${installments.length})`} />
        <CardContent className="p-0">
          {installments.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhuma parcela registrada.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="py-2 px-4 w-10">#</th>
                    <th className="py-2 px-4">Vencimento</th>
                    <th className="py-2 px-4 text-right">Valor</th>
                    <th className="py-2 px-4">Forma de Pagamento</th>
                    <th className="py-2 px-4">Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {installments.map((inst, idx) => (
                    <tr key={inst.material_receipt_installment_id || idx} className="border-b border-slate-100">
                      <td className="py-2 px-4 text-slate-500">{idx + 1}</td>
                      <td className="py-2 px-4">{formatDate(inst.due_date)}</td>
                      <td className="py-2 px-4 text-right font-medium">{formatCurrency(inst.value)}</td>
                      <td className="py-2 px-4">{inst.payment_method_name || '—'}</td>
                      <td className="py-2 px-4 text-slate-600">{inst.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300">
                    <td />
                    <td className="py-2 px-4 text-right font-semibold text-slate-700">Total:</td>
                    <td className="py-2 px-4 text-right font-bold text-emerald-700">
                      {formatCurrency(installments.reduce((s, i) => s + i.value, 0))}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Observações ───────────────────────────────────────── */}
      {(receipt.notes || receipt.internal_notes) && (
        <Card>
          <CardHeader title="Observações" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {receipt.notes && (
                <div>
                  <h4 className="text-xs text-slate-500 uppercase mb-1">Observações</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{receipt.notes}</p>
                </div>
              )}
              {receipt.internal_notes && (
                <div>
                  <h4 className="text-xs text-slate-500 uppercase mb-1">Observações Internas</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{receipt.internal_notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}


      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Excluir Recebimento de Material"
        message="Tem certeza que deseja excluir este recebimento? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
        loading={deleting}
      />

      {/* Lightbox — foto de avaria em tamanho completo */}
      {lightboxUrl && (
        <Modal isOpen={!!lightboxUrl} onClose={closeLightbox} size="lg">
          <div className="relative">
            <button
              className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              onClick={closeLightbox}
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
            <img
              src={lightboxUrl}
              alt="Foto da avaria"
              className="w-full rounded-lg object-contain max-h-[80vh]"
            />
          </div>
        </Modal>
      )}

      {/* Financial Management Modal */}
      {hasLinkedSalesOrders && (
        <FinancialManagementModal
          isOpen={financialModalOpen}
          onClose={() => setFinancialModalOpen(false)}
          receiptId={receipt.material_receipt_id}
          linkedOrders={linkedOrdersForModal}
        />
      )}
    </div>
  );
};
